import React from 'react';
import BrandLink from '@/components/ui/BrandLink';
import Tagline from '@/components/ui/Tagline';
import { motion } from 'framer-motion';
import { FileText, Mail, Phone, Building2, Server, Shield } from 'lucide-react';

export default function MentionsLegales() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <BrandLink />
          <a href="/" className="text-sm text-slate-500 hover:text-slate-700">
            Retour à l'accueil
          </a>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8 md:p-12"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue-100 p-3 rounded-xl">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                Mentions légales
              </h1>
              <p className="text-slate-500 mt-1">
                Dernière mise à jour : 28 décembre 2025
              </p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none">
            {/* Préambule */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8">
              <p className="text-blue-900 font-semibold mb-2">Information légale</p>
              <p className="text-blue-800 leading-relaxed">
                Conformément aux dispositions de la loi n° 2004-575 du 21 juin 2004 pour la confiance dans l'économie 
                numérique (LCEN), il est précisé aux utilisateurs du site TrésoPilot l'identité des différents 
                intervenants dans le cadre de sa réalisation et de son suivi.
              </p>
            </div>

            {/* A - ÉDITEUR */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-6 h-6 text-emerald-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">A. Éditeur du site</h2>
              </div>
              
              <div className="bg-slate-50 rounded-xl p-6 space-y-2">
                <p><strong>Nom commercial :</strong> TrésoPilot</p>
                <p><strong>Éditeur / Responsable :</strong> TrésoPilot</p>
                <p><strong>Statut juridique :</strong> Micro-entreprise (Auto-entrepreneur)</p>
                <p><strong>SIRET :</strong> 952 476 950 00011</p>
                <p><strong>Siège social :</strong> 60 rue François Ier, 75008 Paris, France</p>
                <p><strong>Email :</strong> <a href="mailto:contact@tresopilot.fr" className="text-emerald-600 hover:text-emerald-700">contact@tresopilot.fr</a></p>
                <p><strong>WhatsApp Business :</strong> <a href="tel:+33756901580" className="text-emerald-600 hover:text-emerald-700">+33 7 56 90 15 80</a></p>
                <p className="pt-2 border-t border-slate-200 mt-4">
                  <strong>Description de l'activité :</strong> TrésoPilot est une application web SaaS de pilotage de 
                  trésorerie et de suivi des échéances destinée aux micro-entrepreneurs français. Le service propose 
                  des estimations de cotisations sociales, d'impôts et de charges, ainsi que des rappels d'échéances 
                  URSSAF, TVA et CFE.
                </p>
              </div>
            </section>

            {/* B - DIRECTEUR DE LA PUBLICATION */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-6 h-6 text-emerald-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">B. Directeur de la publication</h2>
              </div>
              
              <div className="bg-emerald-50 rounded-xl p-6">
                <p className="text-slate-700 mb-2">
                  <strong>Directeur de la publication :</strong> Équipe Communication TrésoPilot
                </p>
                <p className="text-slate-700">
                  <strong>Contact :</strong> <a href="mailto:contact@tresopilot.fr" className="text-emerald-600 hover:text-emerald-700">contact@tresopilot.fr</a>
                </p>
                <p className="text-slate-600 text-sm mt-4">
                  Le directeur de la publication est responsable du contenu éditorial du site conformément à la loi 
                  sur la liberté de la presse du 29 juillet 1881.
                </p>
              </div>
            </section>

            {/* C - HÉBERGEMENT */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Server className="w-6 h-6 text-emerald-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">C. Hébergement</h2>
              </div>
              
              <div className="bg-slate-50 rounded-xl p-6 space-y-2">
                <p><strong>Hébergeur :</strong> IONOS SARL</p>
                <p><strong>Forme juridique :</strong> Société à responsabilité limitée (SARL)</p>
                <p><strong>Siège social :</strong> 7, place de la Gare, BP 70109, 57201 Sarreguemines Cedex, France</p>
                <p><strong>SIRET :</strong> 431 303 775 000 16</p>
                <p><strong>Code APE :</strong> 6311Z (Traitement de données, hébergement et activités connexes)</p>
                <p><strong>RCS :</strong> Sarreguemines B 431 303 775</p>
                <p><strong>N° TVA intracommunautaire :</strong> FR 13 431303775</p>
                <p className="pt-2 border-t border-slate-200 mt-4 text-sm text-slate-600">
                  L'hébergeur assure le stockage et la mise à disposition des données du site conformément à ses 
                  obligations légales.
                </p>
              </div>
            </section>

            {/* D - ACCÈS AU SERVICE */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">D. Accès au service</h2>
              
              <p className="text-slate-700 leading-relaxed mb-3">
                TrésoPilot est une application web accessible via le site <strong>https://tresopilot.fr</strong> 
                proposant un service de pilotage de trésorerie et de suivi des échéances pour les micro-entrepreneurs 
                français.
              </p>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">Conditions d'accès</h3>
              <ul className="space-y-2 text-slate-700 list-disc pl-6">
                <li>L'accès au service nécessite la création d'un compte utilisateur</li>
                <li>Certaines fonctionnalités avancées requièrent un abonnement payant (plans Plus ou Pro)</li>
                <li>Une formule gratuite (plan Free) est disponible avec des fonctionnalités limitées</li>
                <li>Un "Pack 60 jours" permet un accès temporaire aux fonctionnalités Premium</li>
              </ul>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">Documents juridiques applicables</h3>
              <div className="bg-blue-50 rounded-xl p-4 space-y-2">
                <p className="text-slate-700">
                  L'utilisation du service est soumise à l'acceptation des documents suivants :
                </p>
                <ul className="space-y-1 text-slate-700 list-disc pl-6">
                  <li><a href="/cgu" className="text-emerald-600 hover:text-emerald-700 font-semibold">Conditions Générales d'Utilisation (CGU)</a></li>
                  <li><a href="/confidentialite" className="text-emerald-600 hover:text-emerald-700 font-semibold">Politique de confidentialité</a></li>
                  <li><a href="/cookies" className="text-emerald-600 hover:text-emerald-700 font-semibold">Politique Cookies</a></li>
                  <li><a href="/disclaimer" className="text-emerald-600 hover:text-emerald-700 font-semibold">Avertissement / Disclaimer</a></li>
                </ul>
              </div>
            </section>

            {/* E - PROPRIÉTÉ INTELLECTUELLE */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">E. Propriété intellectuelle</h2>
              
              <p className="text-slate-700 leading-relaxed mb-3">
                L'ensemble du contenu du site TrésoPilot et de l'application, incluant mais sans s'y limiter, la 
                structure générale, les textes, les images, les graphismes, les logos, les icônes, les sons, les 
                logiciels, les bases de données, ainsi que la marque « TrésoPilot », sont la propriété exclusive de 
                <strong> TrésoPilot</strong> ou font l'objet de droits d'utilisation.
              </p>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">Protection des droits</h3>
              <ul className="space-y-2 text-slate-700 list-disc pl-6 mb-4">
                <li>Tous ces éléments sont protégés par les lois relatives à la propriété intellectuelle et aux droits 
                d'auteur en vigueur en France et à l'international</li>
                <li>Toute reproduction, représentation, modification, publication, adaptation de tout ou partie des 
                éléments du site, quel que soit le moyen ou le procédé utilisé, est interdite sans l'autorisation 
                écrite préalable de l'éditeur</li>
                <li>Toute exploitation non autorisée du site ou de ses éléments sera considérée comme constitutive 
                d'une contrefaçon et poursuivie conformément aux dispositions des articles L.335-2 et suivants du 
                Code de Propriété Intellectuelle</li>
              </ul>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">Usage autorisé</h3>
              <p className="text-slate-700 leading-relaxed">
                L'utilisateur bénéficie d'un droit d'utilisation personnel, non exclusif, non cessible et non 
                transférable du service TrésoPilot, strictement limité à un usage conforme aux Conditions Générales 
                d'Utilisation. Ce droit ne confère aucun droit de propriété intellectuelle sur le service ou ses 
                composants.
              </p>
            </section>

            {/* F - LIMITATION DE RESPONSABILITÉ */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">F. Limitation de responsabilité</h2>
              
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4">
                <p className="text-amber-900 font-semibold mb-2">⚠️ Avertissement important</p>
                <p className="text-amber-800 leading-relaxed">
                  TrésoPilot est un outil d'aide au pilotage de trésorerie. <strong>Il ne constitue en aucun cas un 
                  service d'expertise-comptable, de conseil fiscal ou juridique.</strong> Les estimations, calculs et 
                  dates d'échéances fournis sont indicatifs et ne sauraient se substituer aux informations officielles 
                  fournies par les organismes compétents (URSSAF, impots.gouv.fr, etc.).
                </p>
              </div>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">Responsabilité de l'utilisateur</h3>
              <ul className="space-y-2 text-slate-700 list-disc pl-6 mb-4">
                <li>L'utilisateur demeure seul responsable de l'exactitude des informations qu'il saisit dans l'application</li>
                <li>L'utilisateur est seul responsable du respect de ses obligations déclaratives et de paiement auprès 
                des organismes sociaux et fiscaux</li>
                <li>L'utilisateur doit impérativement vérifier les dates d'échéances et montants exacts sur les portails 
                officiels</li>
              </ul>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">Limitation de responsabilité de l'éditeur</h3>
              <ul className="space-y-2 text-slate-700 list-disc pl-6 mb-4">
                <li>L'éditeur s'efforce de fournir des informations aussi précises que possible. Toutefois, il ne peut 
                garantir l'exactitude, la complétude ou l'actualité des informations diffusées</li>
                <li>L'éditeur ne saurait être tenu responsable des erreurs, omissions, ou des résultats obtenus par 
                l'utilisation du service</li>
                <li>L'éditeur ne saurait être tenu responsable des dommages indirects résultant de l'utilisation ou de 
                l'impossibilité d'utiliser le service (perte de données, perte de chiffre d'affaires, sanctions fiscales, etc.)</li>
                <li>En cas de force majeure ou de difficultés informatiques, réseaux, électriques entraînant des 
                dysfonctionnements du site, l'éditeur ne saurait être tenu responsable</li>
              </ul>

              <h3 className="font-bold text-slate-800 mb-2 mt-4">Disponibilité du service</h3>
              <p className="text-slate-700 leading-relaxed">
                L'éditeur s'efforce d'assurer une disponibilité optimale du service. Toutefois, des interruptions 
                temporaires peuvent survenir pour maintenance, mise à jour, ou en cas de difficultés techniques 
                indépendantes de sa volonté. Ces interruptions ne donneront lieu à aucune indemnisation.
              </p>
            </section>

            {/* G - DONNÉES PERSONNELLES */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-6 h-6 text-emerald-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">G. Données personnelles et RGPD</h2>
              </div>
              
              <p className="text-slate-700 leading-relaxed mb-3">
                L'éditeur accorde une grande importance à la protection des données personnelles de ses utilisateurs. 
                Le traitement des données collectées via le site et l'application est conforme au Règlement Général 
                sur la Protection des Données (RGPD) et à la loi Informatique et Libertés.
              </p>

              <div className="bg-emerald-50 rounded-xl p-6">
                <p className="text-slate-700 mb-3">
                  <strong>Pour en savoir plus :</strong>
                </p>
                <ul className="space-y-2 text-slate-700 list-disc pl-6 mb-4">
                  <li>Consultez notre <a href="/confidentialite" className="text-emerald-600 hover:text-emerald-700 font-semibold">Politique de confidentialité</a> 
                  détaillant les données collectées, les finalités, les durées de conservation et vos droits</li>
                  <li>Consultez notre <a href="/cookies" className="text-emerald-600 hover:text-emerald-700 font-semibold">Politique Cookies</a> 
                  pour gérer vos préférences en matière de traceurs</li>
                </ul>
                <p className="text-slate-700">
                  <strong>Exercice de vos droits RGPD :</strong><br />
                  Pour exercer vos droits (accès, rectification, effacement, limitation, opposition, portabilité), 
                  contactez-nous à : <a href="mailto:contact@tresopilot.fr" className="text-emerald-600 hover:text-emerald-700">contact@tresopilot.fr</a>
                </p>
              </div>
            </section>

            {/* H - COOKIES */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">H. Cookies et traceurs</h2>
              
              <p className="text-slate-700 leading-relaxed mb-3">
                Le site TrésoPilot utilise des cookies et autres technologies de suivi pour améliorer l'expérience 
                utilisateur et analyser l'utilisation du service.
              </p>

              <div className="bg-blue-50 rounded-xl p-4">
                <p className="text-slate-700 mb-2">
                  <strong>Gestion de vos préférences :</strong>
                </p>
                <ul className="space-y-2 text-slate-700 list-disc pl-6">
                  <li>Consultez notre <a href="/cookies" className="text-emerald-600 hover:text-emerald-700 font-semibold">Politique Cookies</a> 
                  pour plus d'informations sur les cookies utilisés</li>
                  <li>Vous pouvez modifier vos préférences à tout moment via le lien "Gérer mes cookies" disponible 
                  dans le footer du site</li>
                  <li>Les cookies strictement nécessaires au fonctionnement du service ne peuvent être désactivés</li>
                </ul>
              </div>
            </section>

            {/* I - CONTACT */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">I. Contact</h2>
              
              <div className="bg-emerald-50 rounded-xl p-6 space-y-3">
                <p className="text-slate-700">
                  Pour toute question concernant les présentes mentions légales, le fonctionnement du site, ou vos 
                  données personnelles :
                </p>
                <div className="flex items-center gap-3">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <Mail className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Email</p>
                    <a href="mailto:contact@tresopilot.fr" className="text-emerald-600 hover:text-emerald-700">
                      contact@tresopilot.fr
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <Phone className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">WhatsApp Business</p>
                    <a href="tel:+33756901580" className="text-emerald-600 hover:text-emerald-700">
                      +33 7 56 90 15 80
                    </a>
                  </div>
                </div>
                <div className="pt-3 border-t border-emerald-200">
                  <p className="text-slate-600 text-sm">
                    <strong>Délai de réponse indicatif :</strong> 24 à 72 heures ouvrées
                  </p>
                </div>
              </div>
            </section>

            {/* Crédits */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">J. Crédits</h2>
              
              <p className="text-slate-700 leading-relaxed mb-3">
                <strong>Conception et développement :</strong> TrésoPilot
              </p>
              <p className="text-slate-700 leading-relaxed">
                <strong>Design :</strong> Réalisé en interne
              </p>
            </section>

            {/* Droit applicable */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">K. Droit applicable et juridiction</h2>
              
              <p className="text-slate-700 leading-relaxed mb-3">
                Les présentes mentions légales sont régies par le droit français. En cas de litige, une solution 
                amiable sera recherchée avant toute action judiciaire.
              </p>
              <p className="text-slate-700 leading-relaxed">
                À défaut de résolution amiable, le litige sera porté devant les tribunaux compétents conformément 
                aux règles de droit commun.
              </p>
            </section>

            <div className="border-t border-slate-200 pt-6 mt-8 text-center text-slate-500 text-sm space-y-2">
              <p><Tagline /></p>
              <p>© {new Date().getFullYear()} TrésoPilot. Tous droits réservés.</p>
              <p className="mt-2">
                Document légal LCEN — Dernière mise à jour : 28 décembre 2025
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}